<template>
  <v-layout column>
    <div class="headline mb-3">{{ $t('title') }}</div>
    <p>{{ $t('intro') }}</p>
    <v-layout row>
      <v-container grid-list-lg fluid align-start>
        <v-layout row wrap>
          <app-start-card :title="$t('sections.deploy.title')" img="deploy">
            <p>
              <i18n path="sections.deploy.content[0]">
                <a place="deploy" :href="docsUrl.cloudstart" target="_blank">{{ $t('sections.deploy.first_collector') }}</a>
              </i18n>
            </p>
            <p>
              <i18n path="sections.deploy.content[1]">
                <a place="platform" :href="docsUrl.quickstart" target="_blank">{{ $t('sections.deploy.platform') }}</a>
              </i18n>
            </p>
            <p>{{ $t('sections.deploy.content[2]') }}</p>
          </app-start-card>
          <app-start-card :title="$t('sections.connect.title')" img="inject">
            <p>
              <i18n path="sections.connect.content[0]">
                <a place="manager" :href="docsUrl.manager" target="_blank">{{ $t('sections.connect.manager')}}</a>
              </i18n>
            </p>
            <p>{{ $t('sections.connect.content[1]') }}</p>
          </app-start-card>
          <app-start-card :title="$t('sections.cross_silo.title')" img="visualize">
            <p>
              <i18n path="sections.cross_silo.content[0]">
                <a place="topology" :href="docsUrl.topology" target="_blank">{{ $t('sections.cross_silo.topology')}}</a>
              </i18n>
            </p>
            <p>{{ $t('sections.cross_silo.content[1]') }}</p>
          </app-start-card>
          <app-start-card :title="$t('sections.agentic.title')" img="analytics">
            <p>
              <i18n path="sections.agentic.content[0]">
                <a place="engine" :href="docsUrl.agentic_engine" target="_blank">{{ $t('sections.agentic.engine') }}</a>
              </i18n>
            </p>
          </app-start-card>
          <app-start-card :title="$t('sections.resolve.title')" img="customize">
            <p>
              <i18n path="sections.resolve.content[0]">
                <a place="assistant" :href="docsUrl.assistant" target="_blank">{{ $t('sections.resolve.assistant') }}</a>
              </i18n>
            </p>
            <p>{{ $t('sections.resolve.content[1]') }}</p>
          </app-start-card>
          <app-start-card :title="$t('sections.manage.title')" img="manage">
            <p>
              <i18n path="sections.manage.content[0]">
                <a place="console" :href="docsUrl.console" target="_blank">{{ $t('sections.manage.console') }}</a>
              </i18n>
            </p>
          </app-start-card>
        </v-layout>
        <app-deploy :open="deploy" @close="closeWizard"></app-deploy>
      </v-container>
    </v-layout>
  </v-layout>
</template>

<script>
import CardComponent from '@/components/start/Card.vue'
import DeployComponent from '@/components/Deploy.vue'
import { docsUrl } from '@/services/docs'
import NetworkService from '@/services/network'

export default {
  data () {
    return {
      selected: null,
      docsUrl: {
        agentic_engine: docsUrl('ai/agentic_engine'),
        assistant: docsUrl('ai-assistant'),
        cloudstart: docsUrl('cloudstart'),
        console: docsUrl('connect'),
        manager: 'https://docs-dev.unryo.com/03_Integrations/03_Types_of_Integrations.html',
        topology: docsUrl('topology'),
        quickstart: docsUrl('quickstart'),
        tag: docsUrl('model', 'use-the-unryo-tagger'),
        tokens: docsUrl('manage', 'manage-tokens'),
        kubernetes: docsUrl('quickstart', 'kubernetes-helm'),
        dashboards: docsUrl('portal'),
        api: docsUrl('api')
      },
      gatherUrl:  docsUrl('quickstart', 'enable-collect'),
      reportsUrl: docsUrl('quickstart', 'start-visualizing-your-data'),
      alertUrl: docsUrl('analytics', 'setting-up-alerts'),
      alertsConsoleUrl: docsUrl('alerts'),
      introductionUrl: docsUrl('introduction'),
      deploy: false
    }
  },
  mounted () {
    NetworkService.nodes().then((nodes) => {
      // grab the first node providing reports and use its url
      const reports = nodes.find((node) => node.group === 'report' && node.url)
      if (reports) {
        this.reportsUrl = reports.url
      }
    })
  },
  methods: {
    openWizard () {
      this.deploy = true
      this.$root.$emit('wizardOpen', 'The wizard is now active.')
    },
    closeWizard () {
      this.deploy = false
      this.$root.$emit('wizardClose', 'The wizard is now closed.')
    }
  },
  components: {
    'app-deploy': DeployComponent,
    'app-start-card': CardComponent
  }
}
</script>

<style lang="scss" scoped>
.container {
  height: 100%;
}

a {
  color: var(--v-accent-base);
  text-decoration: underline;
}
</style>

<i18n>
{
  "en": {
    "title": "Get Started with Unryo",
    "intro": "To learn and get the most out of Unryo, we suggest you complete the following steps:",
    "sections": {
      "agentic": {
        "content": [
          "Unryo's {engine} relieves operations teams by automating investigations. Acting like a domain expert, it interprets issues, gathers the right data, and generates meaningful answers or recommended actions."
        ],
        "engine": "Agentic AI Engine",
        "title": "Power On Agentic AI"
      },
      "analytics": {
        "title": "Activate Analytics & Alerting",
        "content": [
          "Go in the {ui} to pick and choose the analytics rules you want to enable, refine the alerting conditions, and configure alert actions (Slack, Emails, Teams, ...). See and manage your alerts with the {alarms}."
        ],
        "ui": "UI",
        "alarms": "Alarm Console"
      },
      "connect": {
        "title": "Connect Your Data Sources",
        "content": [
          "Your data shouldn’t stay siloed. Unryo acts as a {manager}, seamlessly querying tools, data stores, and infrastructure to deliver cross-domain observability from day one.",
          "Adding a connector is simple: select a template in the UI, and Unryo handles the rest — distributing them securely to collectors."
        ],
        "manager": "Manager of Managers"
      },
      "cross_silo": {
        "content": [
          "Unryo continuously learns dependencies from metrics, logs, and metadata to build an always-up-to-date {topology}.",
          "This model is the trusted backbone for AI agents to map causality, assess impacts, and identify root causes. With rich visualizations, AI insights, SLA tracking, and interactive dashboards, you see the full picture at every layer."
        ],
        "title": "Gain Cross-Silo Visibility",
        "topology": "multi-layer topology"
      },
      "deploy": {
        "title": "Deploy Components",
        "content": [
          "If you're a SaaS customer, {deploy} in a few clicks using the wizard.",
          "If you prefer to host Unryo on prem, use the same wizard to {platform}.",
          "Everything will auto connect instantly and securely. You can then monitor the health of your Unryo deployment straight from the console."
        ],
        "first_collector": "deploy your first collector",
        "platform": "deploy the Unryo platform",
        "topology": "Topology Map"
      },
      "manage": {
        "title": "Manage Your Platform",
        "content": [
          "Unryo is built for near-zero maintenance. The {console} shows real-time health and status across all components, while giving you the flexibility to fine-tune the platform to your environment and requirements."
        ],
        "console": "Connect Console"
      },
      "resolve": {
        "assistant": "AI Assistant",
        "content": [
          "The {assistant} gives users a conversational interface to engage with Unryo’s agents.",
          "It summarizes everything around an alert in one view — why it triggered, its context, correlations, and recommended troubleshooting steps — so teams can resolve faster with confidence."
        ],
        "title": "Resolve Faster"
      },
      "visualize": {
        "title": "Start Visualizing Your Data",
        "content": [
          "Connect to the {portal} and browse your dashboards, analyze your logs, troubleshoot your applications.",
          "We've built numerous ready-to-use {dashboards} that cover the major technologies for you."
        ],
        "dashboards": "dashboards",
        "portal": "Portal"
      }
    }
  }
}
</i18n>
